import { useMemo } from 'react';
import {
  CustomAttributesEntity,
  StoredAttributesRead,
} from 'src/graphql/entities/custom-attributes/custom-attributes-config/custom-attributes-config';
import { CustomAttributeRowData } from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import { parseStoredAttributes } from 'src/graphql/entities/custom-attributes/payload';
import type { CustomAttributesSidePanelData } from 'src/graphql/entities/custom-attributes/side-panel-context';
import { SidePanelPropertyKey } from 'src/graphql/entities/side-panel-personalization';
import { groupCustomAttributes } from './group-custom-attributes';
import { useCanViewCustomAttributes } from './use-can-view-custom-attributes';
import { useCustomAttributeSaving } from './use-custom-attribute-saving';
import { useNormalizedCustomAttributeDefinitions } from './use-normalized-custom-attribute-definitions';

interface UseCustomAttributesSidePanelDataParams {
  entity: CustomAttributesEntity;
  entityId: string;
  /** `undefined` skips the definitions query */
  partnerId: number | undefined;
  canEdit: boolean;
  /** the entity's stored payload — fetched by a panel, read from the cache by a card */
  stored: StoredAttributesRead;
}

/** One entity's custom attributes as side-panel rows keyed by property key (`ca:<key>`). */
export function useCustomAttributesSidePanelData({
  entity,
  entityId,
  partnerId,
  canEdit,
  stored,
}: UseCustomAttributesSidePanelDataParams): CustomAttributesSidePanelData {
  const canView = useCanViewCustomAttributes();

  const {
    visibleDefinitions,
    groups: definedGroups,
    loading: loadingDefinitions,
  } = useNormalizedCustomAttributeDefinitions({ partnerId, entity });

  const { values: parsedValues, error: payloadError } = useMemo(
    () => parseStoredAttributes(stored.customAttributes),
    [stored.customAttributes],
  );

  const { setFieldValue, savingKeys, errorByKey } = useCustomAttributeSaving({
    entity,
    entityId,
  });

  const rowsByPropertyKey = useMemo(() => {
    const rows = new Map<SidePanelPropertyKey, CustomAttributeRowData>();

    if (!canView) {
      return rows;
    }

    const groups = groupCustomAttributes({
      definitions: visibleDefinitions,
      definedGroups,
      parsedValues,
      savingKeys,
      errorByKey,
    });

    for (const group of groups) {
      for (const row of group.rows) {
        rows.set(`ca:${row.definition.key}`, row);
      }
    }

    return rows;
  }, [canView, visibleDefinitions, definedGroups, parsedValues, savingKeys, errorByKey]);

  return useMemo<CustomAttributesSidePanelData>(
    () => ({
      rowsByPropertyKey,
      partnerId,
      loading: canView && (loadingDefinitions || stored.loading),
      payloadError: canView ? payloadError : null,
      disabled: !canEdit || payloadError !== null,
      onSave: setFieldValue,
    }),
    [
      rowsByPropertyKey,
      partnerId,
      canView,
      loadingDefinitions,
      stored.loading,
      payloadError,
      canEdit,
      setFieldValue,
    ],
  );
}
